import PropTypes from "prop-types";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useAuth from "hooks/useAuth";
import { getPostLoginPath } from "utils/adminAccess";
import Loader from "components/Loader";

const PENDING_REFERRAL_KEY = 'pending_referral';

/**
 * Save referral info from URL to localStorage before redirecting to login.
 * This ensures referral tracking works for non-logged-in users.
 */
const saveReferralBeforeRedirect = () => {
  const params = new URLSearchParams(window.location.search);
  const ref = params.get('ref');
  const platform = params.get('platform') || 'unknown';

  if (ref) {
    const pathParts = window.location.pathname.split('/').filter(Boolean);

    if (pathParts.length >= 2) {
      const itemType = pathParts[0];
      const itemId = pathParts[1];

      const pendingReferral = {
        ref,
        itemType,
        itemId,
        platform,
        timestamp: Date.now()
      };
      localStorage.setItem(PENDING_REFERRAL_KEY, JSON.stringify(pendingReferral));
    }
  }
};

export default function RouteGuard({ children, guest = false }) {
  const { isLoggedIn, isInitialized, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    if (isInitialized) {
      if (guest && isLoggedIn) {
        // Logged in user trying to access guest (auth) pages
        navigate(location?.state?.from || getPostLoginPath(user), {
          replace: true,
        });
      } else if (!guest && !isLoggedIn) {
        // Non-logged in user trying to access protected pages
        // Save referral info BEFORE redirecting to login
        saveReferralBeforeRedirect();

        navigate("/login", {
          state: { from: location.pathname },
          replace: true,
        });
      }
    }
  }, [isLoggedIn, isInitialized, guest, navigate, location.pathname, location.state, user]);

  if (!isInitialized) {
    return <Loader />;
  }

  return children;
}

RouteGuard.propTypes = {
  children: PropTypes.node,
  guest: PropTypes.bool,
};
